"use client"

import { useEffect, useState } from "react"
import { motion } from "framer-motion"

import { useMusicContext } from "@/context/musicContext"
import MusicProgressBar from "@/features/footer/controls/MusicProgressBar"

import backupImage from "/image/app/sistema/artista.webp"

type Props = {
  variant?: "footer" | "sidebar"
}

export default function MusicPlayer({
  variant = "sidebar",
}: Props) {
  const {
    audioRef,
    isPlaying,
    currentSong,
    seek,
  } = useMusicContext()

  const isSidebar = variant === "sidebar"

  const [time, setTime] = useState(0)
  const [duration, setDuration] = useState(0)

  const audio = audioRef.current

  const hasTrack = Boolean(
    currentSong?.url ||
    currentSong?.link
  )

  const cover =
    currentSong?.songimg ||
    backupImage

  const title =
    currentSong?.songName ||
    "Sin reproducción"

  useEffect(() => {
    if (!audio) {
      setTime(0)
      setDuration(0)
      return
    }

    const sync = () => {
      setTime(
        Number.isFinite(audio.currentTime)
          ? audio.currentTime
          : 0
      )

      setDuration(
        Number.isFinite(audio.duration)
          ? audio.duration
          : 0
      )
    }

    sync()

    audio.addEventListener(
      "timeupdate",
      sync
    )

    audio.addEventListener(
      "loadedmetadata",
      sync
    )

    audio.addEventListener(
      "durationchange",
      sync
    )

    return () => {
      audio.removeEventListener(
        "timeupdate",
        sync
      )

      audio.removeEventListener(
        "loadedmetadata",
        sync
      )

      audio.removeEventListener(
        "durationchange",
        sync
      )
    }
  }, [audio, currentSong])

  return (
    <div
      className={[
        "w-full",
        "flex",

        isSidebar
          ? "flex-col items-center text-center"
          : "flex-row items-center gap-3",
      ].join(" ")}
    >
      {/* =====================================================
          COVER
      ===================================================== */}

      <div
        className={[
          "relative",
          "shrink-0",
          "overflow-hidden",
          "rounded-xl",
          "bg-white/[0.03]",
          "shadow-xl",

          isSidebar
            ? "mx-4 my-4 aspect-square w-[calc(100%-2rem)]"
            : "h-14 w-14",
        ].join(" ")}
      >
        <motion.img
          key={cover}
          src={cover}
          alt={title}
          draggable={false}
          className="
            absolute
            inset-0
            h-full
            w-full
            object-cover
            select-none
          "
          initial={{
            opacity: 0,
            scale: 1.04,
          }}
          animate={{
            opacity: 1,
            scale: 1,
          }}
          transition={{
            duration: 0.25,
            ease: "easeOut",
          }}
        />

        {isPlaying && hasTrack && (
          <motion.span
            aria-hidden="true"
            className="
              absolute
              right-2
              top-2
              h-1.5
              w-1.5
              rounded-full
              bg-white/60
            "
            animate={{
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 1.2,
              repeat: Infinity,
            }}
          />
        )}
      </div>

      {/* =====================================================
          PROGRESS
      ===================================================== */}

      <div className="w-full px-4">
        {hasTrack ? (
          <MusicProgressBar
            variant={variant}
            currentTime={time}
            duration={duration}
            handleTrackClick={seek}
          />
        ) : (
          <div
            className="
              h-2
              w-full
              rounded-full
              bg-white/[0.08]
            "
          />
        )}
      </div>
    </div>
  )
}